import { ActivityCategory, activityCategoryLabels, ago, daysAgo } from "@sp-crm/core";
import { ActivityRowDisplay, activityTimestampVerb } from "./activity-rows";

export const NOTES_TYPE_LABEL = "Notes";
export const NOTES_FILTER_KEY = "note";

export type RecentActivityFilterKey = ActivityCategory | typeof NOTES_FILTER_KEY;

export interface RecentActivityFeedItem {
    id: string;
    kind: "note" | "task" | "touchpoint" | "visit";
    label: string;
    chipLabel?: string | null;
    category?: ActivityCategory | null;
    text?: string | null;
    noteText?: string | null;
    userName?: string | null;
    occurredAt: string;
}

export const recentActivityItemFilterKey = (
    item: RecentActivityFeedItem,
): RecentActivityFilterKey =>
    item.kind === "note" ? NOTES_FILTER_KEY : (item.category ?? "other");

export const filterRecentActivityItems = <TItem extends RecentActivityFeedItem>(
    items: TItem[],
    selectedKeys: RecentActivityFilterKey[],
): TItem[] => {
    if (selectedKeys.length === 0) {
        return items;
    }
    return items.filter(item => selectedKeys.includes(recentActivityItemFilterKey(item)));
};

const itemSortTime = (item: RecentActivityFeedItem): number =>
    item.occurredAt.includes("T")
        ? new Date(item.occurredAt).getTime()
        : new Date(`${item.occurredAt}T00:00:00`).getTime();

export const sortRecentActivityItems = <TItem extends RecentActivityFeedItem>(
    items: TItem[],
): TItem[] => [...items].sort((a, b) => itemSortTime(b) - itemSortTime(a));

const activityRowChip = (
    item: RecentActivityFeedItem,
): { category: ActivityCategory; label: string } | null => {
    const category = item.category ?? null;
    if (item.chipLabel) {
        return { category: category ?? "other", label: item.chipLabel };
    }
    return category ? { category, label: activityCategoryLabels[category] } : null;
};

const activityRowTitle = (
    item: RecentActivityFeedItem,
    chip: { category: ActivityCategory; label: string } | null,
): string | null => {
    if (!chip) {
        return item.label;
    }
    const typeLabels = [chip.label, activityCategoryLabels[chip.category]];
    return typeLabels.includes(item.label) ? null : item.label;
};

export const recentActivityItemDisplay = (
    item: RecentActivityFeedItem,
): ActivityRowDisplay => {
    const category = item.category ?? null;
    const chip = activityRowChip(item);
    return {
        iconKind:
            item.kind === "note"
                ? "note"
                : item.kind === "task"
                  ? "task"
                  : (category ?? "other"),
        title: activityRowTitle(item, chip),
        titleAccessory: null,
        chip,
        body: item.text ?? null,
        note: item.noteText ?? null,
        byline: item.userName ?? null,
        timestamp:
            item.kind === "note"
                ? ago(new Date(item.occurredAt), "")
                : `${
                      item.kind === "task" || item.kind === "visit"
                          ? "Completed"
                          : activityTimestampVerb(category)
                  } ${
                      item.occurredAt.includes("T")
                          ? ago(new Date(item.occurredAt), "")
                          : daysAgo(new Date(`${item.occurredAt}T00:00:00`), "")
                  }`,
        link: null,
    };
};
